/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hotDeskingActions.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the PBX Extensions module.
────────────────────────────────────────────────────────────────*/

import type { NavigateFunction } from 'react-router-dom';
import { buildApiUrl, getApiHeaders } from '../../config/api';

/**
 * Shared helpers for Hot Desking module actions (used in Grid + Form views).
 */

/**
 * Build the URL used to duplicate a hot desking device in the form.
 * Pattern: /pbx/extensions/hot-desking/new?duplicate={id}
 */
export const getHotDeskDuplicateUrl = (id: string | number): string =>
  `/pbx/extensions/hot-desking/new?duplicate=${id}`;

/**
 * Navigate to the form in duplicate mode for the given device id.
 */
export const navigateToHotDeskDuplicate = (
  navigate: NavigateFunction,
  id: string | number,
): void => {
  navigate(getHotDeskDuplicateUrl(id));
};

/**
 * Low-level API helper to delete a hot desking device by id.
 * UI components are responsible for showing notifications and handling navigation/refresh.
 */
export const deleteHotDeskById = async (id: string | number): Promise<void> => {
  const response = await fetch(
    buildApiUrl(`/api/telephony/hot-desking/${id}`),
    {
      method: 'DELETE',
      headers: getApiHeaders(),
    },
  );

  if (!response.ok) {
    let message = 'Failed to delete hot desk device';
    try {
      const errorData = await response.json();
      if (errorData?.message) {
        message = errorData.message;
      }
    } catch {
      // Ignore JSON parse errors and use default message
    }
    throw new Error(message);
  }
};


